import { useAuthStore } from 'features/auth/stores/authStore';

import { apiClient } from '../client/apiClient';
import { ENDPOINTS } from '../config/endpoints';
import type { ApiResponse } from '../types/common.types';
import type {
  CreateProjectDto,
  Project,
  ProjectMember,
  UpdateProjectDto,
} from '../types/project.types';

const getWorkspaceId = (): string => {
  const workspaceId = useAuthStore.getState().user?.workspaceId;
  if (!workspaceId) throw new Error('No active workspace');
  return workspaceId;
};

export const projectService = {
  getProjects: (params?: {
    name?: string;
    isArchived?: boolean;
    clientId?: string;
    isPublic?: boolean;
    isBillable?: boolean;
  }) => {
    const wid = getWorkspaceId();
    return apiClient.get<ApiResponse<Project[]>>(ENDPOINTS.PROJECTS.LIST(wid), { params });
  },

  getAccessibleProjects: () => {
    const wid = getWorkspaceId();
    return apiClient
      .get<
        ApiResponse<
          {
            projectId: string;
            projectName: string;
            color: string;
            clientName: string | null;
            isBillable: boolean;
          }[]
        >
      >(ENDPOINTS.PROJECTS.ACCESSIBLE(wid))
      .then((r) => ({
        ...r,
        data: {
          ...r.data,
          data: (r.data.data ?? []).map((p) => ({
            id: p.projectId,
            name: p.projectName,
            color: p.color,
            clientName: p.clientName ?? undefined,
            isBillable: p.isBillable,
            isPublic: true,
            isArchived: false,
            createdAt: '',
            updatedAt: '',
          })) as Project[],
        },
      }));
  },

  getProjectsWithTrackedHours: (params?: {
    name?: string;
    isArchived?: boolean;
    clientId?: string;
    isPublic?: boolean;
    isBillable?: boolean;
  }) => {
    const wid = getWorkspaceId();
    return apiClient.get<ApiResponse<Project[]>>(ENDPOINTS.PROJECTS.LIST_WITH_TRACKED_HOURS(wid), {
      params,
    });
  },

  getProject: (id: string) => {
    const wid = getWorkspaceId();
    return apiClient.get<ApiResponse<Project>>(ENDPOINTS.PROJECTS.DETAIL(wid, id));
  },

  createProject: (data: CreateProjectDto) => {
    const wid = getWorkspaceId();
    return apiClient.post<ApiResponse<{ id: string }>>(ENDPOINTS.PROJECTS.CREATE(wid), data);
  },

  updateProject: (id: string, data: UpdateProjectDto) => {
    const wid = getWorkspaceId();
    return apiClient.put<ApiResponse<void>>(ENDPOINTS.PROJECTS.UPDATE(wid, id), data);
  },

  deleteProject: (id: string) => {
    const wid = getWorkspaceId();
    return apiClient.delete<ApiResponse<void>>(ENDPOINTS.PROJECTS.DELETE(wid, id));
  },

  getProjectMembers: (projectId: string) => {
    const wid = getWorkspaceId();
    return apiClient.get<ApiResponse<ProjectMember[]>>(
      ENDPOINTS.PROJECT_MEMBERS.LIST(wid, projectId),
    );
  },

  addProjectMember: (projectId: string, userId: string) => {
    const wid = getWorkspaceId();
    return apiClient.post<ApiResponse<void>>(ENDPOINTS.PROJECT_MEMBERS.ADD(wid, projectId), {
      userId,
    });
  },

  removeProjectMember: (projectId: string, userId: string) => {
    const wid = getWorkspaceId();
    return apiClient.delete<ApiResponse<void>>(
      ENDPOINTS.PROJECT_MEMBERS.REMOVE(wid, projectId, userId),
    );
  },
};
